/* global Word, Office */
import { sandboxedEval } from './sandbox';

/**
 * WordBridge - Office.js code execution engine for Word
 *
 * Core mechanism: The backend Claude agent generates Office.js JavaScript code
 * and structured tool calls. This bridge evaluates that code inside Word.run()
 * context, and implements the structured document tools (surgical find/replace,
 * lists, section reads, blank-paragraph collapse, structural verification)
 * directly against the Word object model.
 *
 * It is the Word analogue of ExcelBridge (which is itself the Excel analogue of
 * PowerPointBridge): the SES sandbox, the SSE tool-result loop, and the
 * context-injection site all drop in unchanged because the constructor shape,
 * the `_safeOfficeRun` wrapper, the `executeCode` SES runner, and
 * `getInitialState()` / `getUserContext()` all keep the same names and
 * return-shape conventions.
 *
 * Paragraphs are addressed by zero-based index (the Word analogue to
 * PowerPoint's slide index and Excel's tabId), which is stable for the duration
 * of a single read/edit turn and is what `read_doc_section` surfaces for
 * follow-up edits.
 *
 * Tools implemented (matching Anthropic's Claude for Word):
 *  - executeCode(code)                  -> eval arbitrary Office.js inside Word.run()
 *  - editDocText({ ... })               -> surgical find/replace (preserves tracked changes)
 *  - editDocList({ ... })               -> create a list or insert one list item
 *  - readDocSection({ ... })            -> read a section by heading or paragraph range
 *  - collapseBlankParagraphs({ ... })   -> collapse runs of empty paragraphs
 *  - verifyDoc()                        -> structural check (styles, headings, lists, blanks)
 *  - screenshotDocument({ pages })      -> render PDF pages for visual QA
 *  - getInitialState()                  -> document metadata for context injection
 *  - getUserContext()                   -> current selection text and style
 */

// =============================================================================
// CONSTANTS (mirrors the reverse-engineered Word bridge)
// =============================================================================

/** Word body.search() hard cap on anchor length (chars). */
const MAX_ANCHOR_CHARS = 255;

/**
 * Cross-product limit for the long-text search path. When old_text exceeds
 * MAX_ANCHOR_CHARS we search its head and tail segments separately, then
 * cross-join the candidate ranges; if heads * tails exceeds this, the anchor
 * overlaps too much boilerplate to verify and we ask for a tighter excerpt.
 */
const LONG_TEXT_CROSS_LIMIT = 256;

/** Output cap (chars) for read_doc_section. */
const MAX_READ_CHARS = 36_000;

/** Delete batch size for collapse_blank_paragraphs (so large docs don't time out). */
const BLANK_PARA_BATCH_SIZE = 20;

/** Outline depth surfaced in metadata and used by heading-section reads. */
const MAX_OUTLINE_HEADING_LEVEL = 3;

/** Tool-enum list_style -> Word built-in style name. */
const LIST_STYLE_MAP = {
  bullet: 'List Bullet',
  number: 'List Number',
};

const DALOOPA_CITATION_ID_RE = /^cx_[A-Za-z0-9_-]{8,128}$/;
const DALOOPA_CITATION_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z)?$/;
const DALOOPA_DATASET_BY_QUERY_CLASS = Object.freeze({
  company_lookup: 'Company Directory',
  series_discovery: 'Financial Series Catalog',
  document_discovery: 'Company Documents',
  fundamentals: 'Company Fundamentals',
  document_content: 'Company Documents',
  prices: 'Market Prices',
  document_search: 'Company Documents',
});

function validateDaloopaCitations(citations) {
  if (!Array.isArray(citations) || citations.length < 1 || citations.length > 100) {
    throw new Error('One to 100 Daloopa citations are required');
  }
  const seenIds = new Set();
  return citations.map((citation) => {
    const isRecord = citation !== null && typeof citation === 'object' && !Array.isArray(citation);
    const asOfDateIsValid = citation?.asOfDate === null || (
      typeof citation?.asOfDate === 'string' &&
      citation.asOfDate.length <= 40 &&
      DALOOPA_CITATION_DATE_RE.test(citation.asOfDate) &&
      Number.isFinite(Date.parse(citation.asOfDate))
    );
    const retrievedAtIsValid = typeof citation?.retrievedAt === 'string' &&
      citation.retrievedAt.length <= 40 &&
      DALOOPA_CITATION_DATE_RE.test(citation.retrievedAt) &&
      Number.isFinite(Date.parse(citation.retrievedAt));
    const expectedDataset = DALOOPA_DATASET_BY_QUERY_CLASS[citation?.queryClass];
    const displayDate = (citation?.asOfDate || citation?.retrievedAt || '').slice(0, 10);
    const expectedMarker = isRecord
      ? `Source: Daloopa | ${citation.dataset} | ${citation.queryClass} | ${displayDate} | ${citation.citationId}`
      : '';
    if (
      !isRecord ||
      citation.providerName !== 'Daloopa' ||
      !DALOOPA_CITATION_ID_RE.test(citation.citationId || '') ||
      seenIds.has(citation.citationId) ||
      !expectedDataset ||
      citation.dataset !== expectedDataset ||
      !asOfDateIsValid ||
      !retrievedAtIsValid ||
      citation.marker !== expectedMarker ||
      citation.marker.length > 360
    ) {
      throw new Error('Daloopa citation metadata is invalid');
    }
    seenIds.add(citation.citationId);
    return Object.freeze({
      citationId: citation.citationId,
      marker: expectedMarker,
    });
  });
}

function validateDaloopaWordTarget(target) {
  const keys = target && typeof target === 'object' && !Array.isArray(target)
    ? Object.keys(target).sort().join(',')
    : '';
  if (
    keys !== 'app,location' ||
    target.app !== 'word' ||
    target.location !== 'document_end'
  ) {
    throw new Error('A valid Word citation target is required');
  }
}

function validateFinanceWordText(value, { allowEmpty = false } = {}) {
  const hasBlockedControl = typeof value === 'string' && [...value].some((character) =>
    character === '\r' || character === '\n' || character.charCodeAt(0) === 0
  );
  return typeof value === 'string' &&
    value.length <= 2000 &&
    (allowEmpty || value.length > 0) &&
    !hasBlockedControl;
}

function validateFinanceWordContentRequest(args) {
  const keys = args && typeof args === 'object' && !Array.isArray(args)
    ? Object.keys(args)
    : [];
  if (
    keys.length < 2 ||
    keys.some((key) => !['target', 'paragraphs', 'table'].includes(key)) ||
    !keys.includes('target') ||
    (args.paragraphs === undefined && args.table === undefined)
  ) {
    throw new Error('Finance Word content input is invalid');
  }
  validateDaloopaWordTarget(args.target);

  let paragraphs = [];
  if (args.paragraphs !== undefined) {
    if (
      !Array.isArray(args.paragraphs) ||
      args.paragraphs.length < 1 ||
      args.paragraphs.length > 100 ||
      args.paragraphs.some((paragraph) => !validateFinanceWordText(paragraph))
    ) {
      throw new Error('Finance Word paragraphs are invalid');
    }
    paragraphs = [...args.paragraphs];
  }

  let table = null;
  if (args.table !== undefined) {
    const tableKeys = args.table && typeof args.table === 'object' && !Array.isArray(args.table)
      ? Object.keys(args.table).sort().join(',')
      : '';
    const headers = args.table?.headers;
    const rows = args.table?.rows;
    if (
      tableKeys !== 'headers,rows' ||
      !Array.isArray(headers) ||
      headers.length < 1 ||
      headers.length > 30 ||
      headers.some((header) => !validateFinanceWordText(header)) ||
      !Array.isArray(rows) ||
      rows.length < 1 ||
      rows.length > 500 ||
      rows.some((row) =>
        !Array.isArray(row) ||
        row.length !== headers.length ||
        row.some((cell) => !validateFinanceWordText(cell, { allowEmpty: true }))
      )
    ) {
      throw new Error('Finance Word table is invalid');
    }
    table = Object.freeze({
      headers: [...headers],
      rows: rows.map((row) => [...row]),
    });
  }

  return Object.freeze({
    target: args.target,
    paragraphs,
    table,
  });
}

/**
 * Matches a styleBuiltIn string for a heading paragraph (Heading1..Heading9).
 * Used to build the outline and to bound heading-based section reads.
 */
const HEADING_STYLE_RE = /^Heading([1-9])$/;

/**
 * A blank paragraph is "structurally meaningful" (and so NOT collapsible) if its
 * OOXML carries a drawing, picture, embedded object, section break, bookmark,
 * tracked-change marker, or comment range. Such runs are skipped during collapse.
 */
const STRUCTURAL_OOXML_RE =
  /<w:drawing|<w:pict|<w:object|<w:sectPr|<w:bookmark|<w:del|<w:ins|<w:commentRange/;

/**
 * Matches any tracked-change (revision) markup in a paragraph's OOXML. Used by
 * the post-edit TrackedChangeGuard: if Track Changes is on but the edited
 * range carries none of these, the edit landed directly instead of as a
 * redline and we surface a warning so the model can tell the user.
 */
const TRACKED_CHANGE_OOXML_RE =
  /<w:(ins|del|rPrChange|pPrChange|tblPrChange|tblPrExChange|trPrChange|tcPrChange|cellIns|cellDel|cellMerge|sectPrChange|numberingChange|moveFrom|moveTo)\b/;

const MEANINGFUL_BODY_OOXML_RE =
  /<(?:w:tbl|w:sdt|w:drawing|w:pict|w:object|w:altChunk|w:fldSimple|w:instrText|w:br|m:oMath|v:shape)\b/i;

/**
 * Decide whether a Word body is safe for append-style template insertion.
 * Text plus non-text structures are checked so image-only and scaffolded
 * documents cannot be mistaken for blank documents.
 */
export function hasMeaningfulWordBodyContent(text, bodyOoxml) {
  if (String(text || '').trim().length > 0) return true;
  return MEANINGFUL_BODY_OOXML_RE.test(String(bodyOoxml || ''));
}

// =============================================================================
// TIMEOUT GUARD (identical contract to PowerPointBridge / ExcelBridge)
// =============================================================================

const TOOL_TIMEOUT_MS = 80_000; // 80s - under backend's 90s timeout

/**
 * Race a promise against a timeout. If the promise doesn't resolve
 * within the given ms, reject with a descriptive error.
 * @param {Promise} promise - The promise to race
 * @param {number} ms - Timeout in milliseconds
 * @param {string} label - Label for the error message
 * @returns {Promise} - Resolves with the original promise or rejects on timeout
 */
function withTimeout(promise, ms, label = 'Office.js call') {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms / 1000}s - try a simpler operation or break it into smaller steps`));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function isSesInitializationError(error) {
  const message = String(error?.message || error || '');
  return message.includes('Compartment') ||
    message.includes('lockdown') ||
    message.includes('SES');
}

function createSesUnavailableError(toolName) {
  return new Error(
    `SES sandbox unavailable - ${toolName} is disabled until the sandbox initializes successfully`
  );
}

// =============================================================================
// UTILITY: smart/straight quote normalization (curly vs straight auto-handled)
// =============================================================================

/**
 * Normalize smart punctuation (curly quotes, en/em dashes, ellipsis) to its
 * straight ASCII equivalent. Used for fuzzy anchor comparison so a model anchor
 * typed with straight quotes still matches text Word auto-corrected to curly.
 * @param {string} text - Input text
 * @returns {string} Text with smart punctuation flattened to straight
 */
function normalizeToStraightPunct(text) {
  return String(text || '').replace(/[‘’“”–\u2014…]/g, (ch) => {
    if (ch === '‘' || ch === '’') return "'";
    if (ch === '“' || ch === '”') return '"';
    if (ch === '–') return '-';
    if (ch === '\u2014') return '-';
    return '...'; // ellipsis
  });
}

/**
 * Compare two strings ignoring smart-vs-straight punctuation differences.
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {boolean} True if equal after straight-punctuation normalization
 */
function fuzzyTextMatch(a, b) {
  return normalizeToStraightPunct(a) === normalizeToStraightPunct(b);
}

/**
 * Parse a built-in style name ("Heading2") into its numeric heading level.
 * @param {string} styleBuiltIn - Built-in style name
 * @returns {number|null} 1..9, or null if not a heading style
 */
function parseHeadingLevel(styleBuiltIn) {
  const match = HEADING_STYLE_RE.exec(String(styleBuiltIn || ''));
  return match ? parseInt(match[1], 10) : null;
}

/** Strip a leading list/numbering prefix ("1. ", "a) ", "- ") for fuzzy heading match. */
function stripNumbering(text) {
  return String(text || '').replace(/^\s*([0-9]+[.)]|[a-z][.)]|[-•*])\s+/i, '').trim();
}

/**
 * Detects whether a string contains straight punctuation (quotes, double
 * hyphen, or "...") that Word may have auto-corrected to smart equivalents.
 * Used to decide whether a smart-quote search fallback is worth trying.
 */
const STRAIGHT_PUNCT_RE = /['"]|--|\.{3}/;

/** Word-boundary characters that precede an opening (vs closing) smart quote. */
const WORD_BOUNDARY_RE = /[\s([{‘“–—-]/;

/**
 * Convert straight quotes/dashes to their smart Unicode equivalents. Mirrors
 * what Word's AutoCorrect does, so a model anchor typed with straight
 * punctuation can be re-searched against the curly text Word actually stores.
 * @param {string} text - Input text with straight punctuation
 * @returns {string} Text with straight punctuation smart-ified
 */
function normalizeToSmartPunct(text) {
  const isBoundary = (ch) => !ch || WORD_BOUNDARY_RE.test(ch);
  return String(text || '')
    .replace(/\.{3}/g, '…')
    .replace(/--/g, '—')
    .replace(/"/g, (_, pos, str) => (isBoundary(str[pos - 1]) ? '“' : '”'))
    .replace(/'/g, (_, pos, str) => (isBoundary(str[pos - 1]) ? '‘' : '’'));
}

/**
 * Split multi-line old_text into one search line per paragraph, dropping a
 * single trailing newline so a clean "...text\n" anchor stays single-line.
 * @param {string} text - Possibly multi-line anchor text
 * @returns {string[]} Lines (one per paragraph)
 */
function splitParagraphLines(text) {
  return String(text || '').replace(/[\r\n]+$/, '').split(/\r\n|\r|\n/);
}

/**
 * True when old_text is "simple": a single line within the search API cap, so
 * it can go through a plain body.search rather than the multi-paragraph walker.
 * @param {string} text - Anchor text
 * @returns {boolean}
 */
function isSimpleOldText(text) {
  const lines = splitParagraphLines(text);
  return lines.length === 1 && lines[0].length <= MAX_ANCHOR_CHARS;
}

/**
 * Truncate a string for inclusion in an error message (keeps the first 40
 * chars and appends an ellipsis when clipped).
 * @param {string} text - Text to clip
 * @returns {string}
 */
function truncateForDisplay(text) {
  const s = String(text || '');
  return s.length > 40 ? `${s.slice(0, 40)}…` : s;
}

/**
 * Compute the minimal diff bounds between old and new text: the length of the
 * shared leading prefix (`pre`), the shared trailing suffix (`suf`), and the
 * changed middles (`aMid` from old_text, `bMid` from new_text). This is what
 * lets edit_doc_text narrow the replaced range to the word/sentence that
 * actually changed, so Track Changes records a tight revision.
 * @param {string} oldText - Original text
 * @param {string} newText - Replacement text
 * @returns {{ pre: number, suf: number, aMid: string, bMid: string }}
 */
function computeDiffBounds(oldText, newText) {
  let pre = 0;
  const preLimit = Math.min(oldText.length, newText.length);
  while (pre < preLimit && oldText[pre] === newText[pre]) pre++;

  let suf = 0;
  const sufLimit = Math.min(oldText.length - pre, newText.length - pre);
  while (
    suf < sufLimit &&
    oldText[oldText.length - 1 - suf] === newText[newText.length - 1 - suf]
  ) suf++;

  return {
    pre,
    suf,
    aMid: oldText.slice(pre, oldText.length - suf),
    bMid: newText.slice(pre, newText.length - suf),
  };
}

// =============================================================================
// MAIN CLASS
// =============================================================================

export class WordBridge {
  constructor({ isOfficeOnline = false } = {}) {
    this._ready = false;
    this._lastError = null;
    this._isOfficeOnline = isOfficeOnline;
    // Office Online serialization queue (1.1) - prevents concurrent Word.run() crashes
    this._onlineRunQueue = Promise.resolve();
  }

  /**
   * Safe wrapper around Word.run() that serializes calls on Office Online.
   * Desktop can handle concurrent calls; Office Online crashes with GeneralException.
   * @param {Function} fn - Callback receiving (context)
   * @returns {Promise<any>}
   */
  _safeOfficeRun(fn, { timeoutMs = TOOL_TIMEOUT_MS, label = 'Office.run', signal = null } = {}) {
    // Bound EVERY run with a timeout measured from when the run ACTUALLY starts
    // (after the queue clears, on Office web). This is the key to preventing
    // cascades: a slow or hung Word.run can't consume a later call's time
    // budget, and the serialized queue always advances within `timeoutMs`.
    // (async () => ...) ensures even a synchronous throw becomes a rejection.
    const exec = () => {
      if (signal?.aborted) {
        const error = new Error(`${label} was cancelled before Office execution started`);
        error.name = 'AbortError';
        return Promise.reject(error);
      }
      return withTimeout((async () => Word.run(fn))(), timeoutMs, label);
    };

    // Desktop: runs are reliable and may overlap safely - no serialization.
    if (!this._isOfficeOnline) {
      return exec();
    }

    // Office web: concurrent Word.run calls crash with GeneralException, so
    // serialize them. Chain each run after the previous one SETTLES. The chain
    // NEVER rejects (so one failure can't break the queue for everyone) and
    // NEVER stays pending (exec is always timeout-bounded), so the queue
    // self-heals within `timeoutMs` even if a run hangs.
    const run = this._onlineRunQueue.then(exec, exec);
    this._onlineRunQueue = run.then(() => {}, () => {});
    return run;
  }

  // ===========================================================================
  // INTERNAL HELPERS (paragraph loading, heading detection, heading lookup)
  // ===========================================================================

  /**
   * Load the body's paragraph collection with text + style and sync. Shared by
   * the section read, collapse, verify, and metadata paths.
   * @param {Word.RequestContext} context - The active request context
   * @param {string} [fields='items/text, items/styleBuiltIn'] - load string
   * @returns {Promise<Word.ParagraphCollection>} The loaded paragraph collection
   */
  async _getParagraphs(context, fields = 'items/text, items/styleBuiltIn') {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load(fields);
    await context.sync();
    return paragraphs;
  }

  /**
   * Decide whether a built-in style name is a heading (Heading1..Heading9).
   * @param {string} style - Built-in style name
   * @returns {boolean}
   */
  _isHeadingStyle(style) {
    return parseHeadingLevel(style) != null;
  }

  /**
   * Find the index of the heading paragraph whose text matches `heading`, plus
   * the index of the next heading of the same-or-higher level (the section end).
   * Tries exact match first, then a numbering-stripped fuzzy match.
   * @param {Array<{ text: string, styleBuiltIn: string }>} paragraphs - Loaded paragraphs
   * @param {string} heading - Heading text to locate
   * @returns {{ start: number, end: number, level: number }|null}
   */
  _findHeadingParagraph(paragraphs, heading) {
    const wanted = String(heading || '').trim();
    if (!wanted) return null;

    let start = -1;
    let level = 0;

    // Pass 1: exact (fuzzy-punctuation) text match on a heading-styled paragraph.
    for (let i = 0; i < paragraphs.length; i++) {
      const lvl = parseHeadingLevel(paragraphs[i].styleBuiltIn);
      if (lvl == null) continue;
      if (fuzzyTextMatch(String(paragraphs[i].text || '').trim(), wanted)) {
        start = i;
        level = lvl;
        break;
      }
    }

    // Pass 2: numbering-stripped fuzzy match (handles "1. Scope" vs "Scope").
    if (start === -1) {
      for (let i = 0; i < paragraphs.length; i++) {
        const lvl = parseHeadingLevel(paragraphs[i].styleBuiltIn);
        if (lvl == null) continue;
        if (fuzzyTextMatch(stripNumbering(paragraphs[i].text), stripNumbering(wanted))) {
          start = i;
          level = lvl;
          break;
        }
      }
    }

    if (start === -1) return null;

    // End at the next heading of the same or higher (lower-numbered) level.
    let end = paragraphs.length;
    for (let i = start + 1; i < paragraphs.length; i++) {
      const lvl = parseHeadingLevel(paragraphs[i].styleBuiltIn);
      if (lvl != null && lvl <= level) {
        end = i;
        break;
      }
    }
    return { start, end, level };
  }

  // ===========================================================================
  // CORE: Execute arbitrary Office.js code
  // ===========================================================================

  /**
   * Execute arbitrary Office.js code inside Word.run().
   * Uses SES Compartment sandbox for security, exactly like
   * PowerPointBridge.executeCode but with the Word host namespace.
   *
   * @param {string} code - JavaScript code string to execute inside Word.run()
   * @returns {Promise<any>} - Result of the code execution
   */
  async executeCode(code) {
    if (typeof Word === 'undefined') {
      throw new Error('Word API not available');
    }

    return this._safeOfficeRun(async (context) => {
      let result;
      try {
        // Primary path: SES Compartment sandbox
        result = await sandboxedEval(code, {
          context,
          Word,
        });
      } catch (sesError) {
        if (isSesInitializationError(sesError)) {
          throw createSesUnavailableError('execute_office_js');
        }
        throw sesError;
      }
      await context.sync();
      return result;
    }, { timeoutMs: TOOL_TIMEOUT_MS, label: 'execute_office_js' });
  }

  // ===========================================================================
  // TOOL: edit_doc_text (surgical find/replace, preserves tracked changes)
  // ===========================================================================

  /**
   * Surgical find/replace that produces word/sentence-level edits while
   * preserving the document's tracked-changes mode. It does NOT toggle Track
   * Changes: if tracking is on, Word records the replace as a redline; if off,
   * it lands directly. Font formatting on the matched range is snapshotted and
   * reapplied so the replacement keeps the surrounding style.
   *
   * Matching is robust in three ways:
   *  - Smart-vs-straight quotes: an exact search is tried first, then a
   *    smart-quote-normalized fallback.
   *  - Long anchors (> 255 chars): the head and tail 255-char segments are
   *    searched separately and cross-joined, so anchors past the search API
   *    cap still resolve (no hard reject).
   *  - Multi-paragraph anchors (old_text contains newlines): the first line is
   *    located, then each following paragraph is walked forward and fuzzy
   *    compared so the span must match line for line.
   *
   * The replacement itself is minimized via computeDiffBounds: only the changed
   * word/sentence sub-range is replaced (or text is inserted/appended) so Track
   * Changes records a tight revision instead of a whole-paragraph
   * delete-and-reinsert. Before a full Replace it guards against destroying
   * inline references (fields, bookmarks, foot/endnotes) and afterward warns if
   * tracking was on but the edit landed without revision markup.
   *
   * @param {Object} params
   * @param {string} params.old_text - Exact text to replace (must match once)
   * @param {string} params.new_text - Replacement ('' deletes the range)
   * @returns {Promise<Object>} - { success, replacements, strategy?, warning?, notFound?, error? }
   */
  async editDocText({ old_text, new_text }) {
    if (typeof Word === 'undefined') {
      throw new Error('Word API not available');
    }
    if (typeof old_text !== 'string' || old_text.length === 0) {
      throw new Error('old_text is required and must be non-empty');
    }
    if (typeof new_text !== 'string') {
      throw new Error('new_text is required (use an empty string to delete the range)');
    }

    return this._safeOfficeRun(async (context) => {
      // Locate the unique range (handles short, long, and multi-paragraph text).
      const found = await this._findUniqueRange(context, old_text);
      if (found.error) {
        // No-match is reported as a soft notFound; ambiguity/structure are hard
        // failures. Both are surfaced structurally so the model can act on them.
        if (found.reason === 'no_match') {
          return { success: true, replacements: 0, notFound: true, error: found.error };
        }
        return { success: false, replacements: 0, error: found.error };
      }
      const range = found.range;

      // Compute the narrowest replacement spec for clean tracked changes.
      const spec = await this._computeReplaceLocation(context, range, old_text, new_text);
      const isReplace = spec.location === Word.InsertLocation.replace;

      // Inline-reference guard (best-effort): refuse a full Replace that would
      // destroy fields / bookmarks / foot/endnotes inside the range.
      if (isReplace) {
        try {
          const refWarning = await this._checkInlineReferences(context, spec.range);
          if (refWarning) {
            return { success: false, replacements: 0, error: refWarning };
          }
        } catch (e) {
          // Inline-ref probe unsupported on this host; proceed with the replace.
        }
      }

      // Snapshot font so a full Replace keeps the matched range's formatting.
      // (Insert/append paths inherit the adjacent run's formatting natively.)
      let fontSnapshot = null;
      if (isReplace) {
        spec.range.font.load('bold, italic, underline, color, size, name, highlightColor');
        await context.sync();
        fontSnapshot = {
          bold: spec.range.font.bold,
          italic: spec.range.font.italic,
          underline: spec.range.font.underline,
          color: spec.range.font.color,
          size: spec.range.font.size,
          name: spec.range.font.name,
          highlightColor: spec.range.font.highlightColor,
        };
      }

      // Apply. Normalize \n -> \r so multi-paragraph text creates real Word
      // paragraph marks. We do NOT touch document.changeTrackingMode: tracked
      // changes are preserved exactly as the user configured them.
      const insertText = String(spec.text).replace(/\r?\n/g, '\r');
      const resultRange = spec.range.insertText(insertText, spec.location);
      await context.sync();

      // Restore font properties (skip "Mixed" sentinels) for full replaces.
      if (isReplace) {
        try {
          this._restoreFont(spec.range, fontSnapshot);
          await context.sync();
        } catch (e) {
          // Best-effort: a heterogeneous range may reject a uniform restore.
        }
      }

      // Post-edit TrackedChangeGuard (best-effort): if tracking is on but the
      // edited paragraph shows no revision markup, the edit landed directly.
      let warning;
      try {
        warning = await this._checkTrackedChangeWarning(context, resultRange);
      } catch (e) {
        warning = undefined;
      }

      const out = { success: true, replacements: 1, strategy: spec.strategy };
      if (warning) out.warning = warning;
      return out;
    }, { label: 'edit_doc_text' });
  }

  // ---------------------------------------------------------------------------
  // edit_doc_text search pipeline (short / long / multi-paragraph)
  // ---------------------------------------------------------------------------

  /**
   * Search the body for `text`, returning an array of candidate ranges (or an
   * `{ unsearchable }` sentinel). Short text (<= 255 chars) searches directly
   * with a smart-quote fallback. Long text searches the head and tail 255-char
   * segments separately (each with a smart variant), guards against homogeneous
   * runs and boilerplate explosions, then cross-joins via expandTo and filters
   * by fuzzy (straight-normalized) equality.
   * @param {Word.RequestContext} context - The active request context
   * @param {string} text - Search text (one paragraph line)
   * @returns {Promise<Word.Range[]|{ unsearchable: string }>}
   */
  async _searchBodyForText(context, text) {
    const body = context.document.body;

    // Short-text path: direct search, then a smart-quote fallback.
    if (text.length <= MAX_ANCHOR_CHARS) {
      const results = body.search(text, { matchCase: true });
      results.load('items');
      await context.sync();

      if (results.items.length === 0 && STRAIGHT_PUNCT_RE.test(text)) {
        const smart = normalizeToSmartPunct(text);
        if (smart !== text) {
          const smartResults = body.search(smart, { matchCase: true });
          smartResults.load('items');
          await context.sync();
          return [...smartResults.items];
        }
      }
      return [...results.items];
    }

    // Long-text path: head + tail segments.
    const head = text.slice(0, MAX_ANCHOR_CHARS);
    const tail = text.slice(-MAX_ANCHOR_CHARS);

    if (head === tail) {
      return {
        unsearchable:
          "old_text is a long homogeneous run (e.g., a signature blank or rule line) and can't be located by content alone. " +
          'Anchor on adjacent distinctive text - include the label or sentence next to it in old_text.',
      };
    }

    const smartHead = normalizeToSmartPunct(head);
    const smartTail = normalizeToSmartPunct(tail);

    const headResults = body.search(head, { matchCase: true });
    const tailResults = body.search(tail, { matchCase: true });
    const smartHeadResults = smartHead !== head ? body.search(smartHead, { matchCase: true }) : null;
    const smartTailResults = smartTail !== tail ? body.search(smartTail, { matchCase: true }) : null;

    headResults.load('items');
    tailResults.load('items');
    if (smartHeadResults) smartHeadResults.load('items');
    if (smartTailResults) smartTailResults.load('items');
    await context.sync();

    const allHeads = [...headResults.items, ...(smartHeadResults ? smartHeadResults.items : [])];
    const allTails = [...tailResults.items, ...(smartTailResults ? smartTailResults.items : [])];

    if (allHeads.length === 0 || allTails.length === 0) return [];

    if (allHeads.length * allTails.length > LONG_TEXT_CROSS_LIMIT) {
      return {
        unsearchable:
          `old_text overlaps boilerplate that appears widely in the document (start x${allHeads.length}, end x${allTails.length}) - ` +
          'too many candidate spans to verify. Shorten old_text to the distinctive part of the change, ' +
          'or include the section heading on a line before it.',
      };
    }

    // Cross-join: expand each head to each tail, load text, then fuzzy-filter.
    const candidates = [];
    for (const h of allHeads) {
      for (const t of allTails) {
        const expanded = h.expandTo(t);
        expanded.load('text');
        candidates.push(expanded);
      }
    }
    await context.sync();
    return candidates.filter((r) => fuzzyTextMatch(r.text, text));
  }

  /**
   * Find a contiguous multi-paragraph span matching `lines` (each line = the
   * full text of one paragraph). Locates line[0], then for each candidate walks
   * forward with getNextOrNullObject() comparing each paragraph's trimmed text
   * by fuzzy match. Requires exactly one full span and reports a precise error
   * (with line number and expected-vs-actual excerpts) otherwise.
   * @param {Word.RequestContext} context - The active request context
   * @param {string[]} lines - One full-paragraph line per array entry
   * @returns {Promise<{ range: Word.Range }|{ error: string, reason: string }>}
   */
  async _findMultiParagraphRange(context, lines) {
    const firstLineMatches = await this._searchBodyForText(context, lines[0]);
    if (!Array.isArray(firstLineMatches)) {
      return { error: firstLineMatches.unsearchable, reason: 'unsearchable' };
    }
    if (firstLineMatches.length === 0) {
      return {
        error: `old_text line 1 ("${truncateForDisplay(lines[0])}") not found in the document ` +
          '(curly/straight quote variants are matched automatically). Re-read the section to get the exact text.',
        reason: 'no_match',
      };
    }

    // For each first-line match, load its first paragraph and the following
    // paragraphs (one getNextOrNullObject per remaining line).
    const matchData = firstLineMatches.map((matchRange) => {
      const firstPara = matchRange.paragraphs.getFirst();
      firstPara.load('text');
      let lastPara = matchRange.paragraphs.getLast();
      const nextParas = [];
      for (let i = 1; i < lines.length; i++) {
        lastPara = lastPara.getNextOrNullObject();
        lastPara.load('text, isNullObject');
        nextParas.push(lastPara);
      }
      return { firstPara, next: nextParas };
    });
    await context.sync();

    const fullMatches = [];
    let firstMismatch = null;

    for (let m = 0; m < firstLineMatches.length; m++) {
      const firstParaText = String(matchData[m].firstPara.text || '').trim();
      if (!fuzzyTextMatch(firstParaText, lines[0].trim())) {
        if (!firstMismatch) firstMismatch = { line: 1, expected: lines[0].trim(), actual: firstParaText };
        continue;
      }

      let mismatch = null;
      for (let i = 1; i < lines.length; i++) {
        const nextPara = matchData[m].next[i - 1];
        const nextText = nextPara.isNullObject ? '(end of document)' : String(nextPara.text || '').trim();
        if (nextPara.isNullObject || !fuzzyTextMatch(nextText, lines[i].trim())) {
          mismatch = { line: i + 1, expected: lines[i].trim(), actual: nextText };
          break;
        }
      }
      if (mismatch) {
        if (!firstMismatch) firstMismatch = mismatch;
        continue;
      }

      // Every line matched: expand the anchor to cover the last paragraph.
      const lastNext = matchData[m].next[lines.length - 2];
      fullMatches.push(firstLineMatches[m].expandTo(lastNext.getRange('Content')));
    }

    if (fullMatches.length === 1) return { range: fullMatches[0] };
    if (fullMatches.length > 1) {
      return {
        error: `old_text matched ${fullMatches.length} paragraph spans - it must be unique. ` +
          'Add another paragraph or more of line 1 to disambiguate.',
        reason: 'multi_match',
      };
    }
    return {
      error: firstMismatch
        ? `old_text line ${firstMismatch.line} ("${truncateForDisplay(firstMismatch.expected)}") doesn't match the ` +
          `${firstMismatch.line === 1 ? 'full' : 'next'} paragraph ("${truncateForDisplay(firstMismatch.actual)}"). ` +
          'Each newline-separated line must be one FULL paragraph. Re-read with read_doc_section to get exact paragraph text.'
        : 'old_text not found as a contiguous paragraph span.',
      reason: 'no_match',
    };
  }

  /**
   * Find the unique range matching `oldText`. Delegates to the multi-paragraph
   * walker when oldText spans newlines, otherwise searches the single line and
   * enforces uniqueness.
   * @param {Word.RequestContext} context - The active request context
   * @param {string} oldText - Anchor text (may be multi-line / long)
   * @returns {Promise<{ range: Word.Range }|{ error: string, reason: string }>}
   */
  async _findUniqueRange(context, oldText) {
    const lines = splitParagraphLines(oldText);
    if (lines.length > 1) return this._findMultiParagraphRange(context, lines);

    const results = await this._searchBodyForText(context, lines[0]);
    if (!Array.isArray(results)) {
      return { error: results.unsearchable, reason: 'unsearchable' };
    }
    if (results.length === 0) {
      return {
        error: 'old_text not found in the document (curly/straight quote variants are matched automatically). ' +
          'Check for exact spelling and whitespace, or re-read the section with read_doc_section to get the exact text.',
        reason: 'no_match',
      };
    }
    if (results.length > 1) {
      return {
        error: `old_text matched ${results.length} ranges - it must be unique. ` +
          'Add surrounding words to disambiguate (e.g., include the start of the next sentence or a distinctive phrase nearby).',
        reason: 'multi_match',
      };
    }
    return { range: results[0] };
  }

  /**
   * Given a matched range and the old/new text, compute the narrowest possible
   * replacement spec so Track Changes records a tight, readable revision rather
   * than a whole-paragraph delete-and-reinsert. Strategies, in order:
   *  1. No common prefix/suffix: full range Replace.
   *  2. Changed middle >= 4 chars: sub-search aMid inside the matched range and
   *     Replace just that sub-range with bMid (skipped if aMid > 255 or has \n).
   *  3. Pure append (aMid empty, whole old_text is prefix): insert bMid After.
   *  4. Pure insertion (aMid empty, prefix >= 4): sub-search the prefix and
   *     insert bMid After it.
   * Falls back to a full Replace whenever a sub-search is not unique.
   * @param {Word.RequestContext} context - The active request context
   * @param {Word.Range} matchedRange - The range found for old_text
   * @param {string} oldText - Original text
   * @param {string} newText - Replacement text
   * @returns {Promise<{ range: Word.Range, text: string, location: Word.InsertLocation, strategy: string }>}
   */
  async _computeReplaceLocation(context, matchedRange, oldText, newText) {
    const fullReplace = {
      range: matchedRange,
      text: newText,
      location: Word.InsertLocation.replace,
      strategy: 'full_replace',
    };

    const { pre, suf, aMid, bMid } = computeDiffBounds(oldText, newText);
    if (pre === 0 && suf === 0) return fullReplace;

    const trySubSearch = async (searchText) => {
      if (searchText.length > MAX_ANCHOR_CHARS || searchText.includes('\n')) return null;
      const hits = matchedRange.search(searchText, { matchCase: true });
      hits.load('items');
      await context.sync();
      return hits.items.length === 1 ? hits.items[0] : null;
    };

    // Strategy 2: replace just the changed middle.
    if (aMid.length >= 4) {
      const subRange = await trySubSearch(aMid);
      if (subRange) {
        return { range: subRange, text: bMid, location: Word.InsertLocation.replace, strategy: 'sub_replace' };
      }
    } else if (aMid.length === 0 && pre >= 4) {
      // Strategy 3: pure append (entire old_text is the shared prefix).
      if (pre === oldText.length) {
        return { range: matchedRange, text: bMid, location: Word.InsertLocation.after, strategy: 'append' };
      }
      // Strategy 4: pure insertion after the matched prefix.
      const prefixRange = await trySubSearch(oldText.slice(0, pre));
      if (prefixRange) {
        return { range: prefixRange, text: bMid, location: Word.InsertLocation.after, strategy: 'insert_after_prefix' };
      }
    }

    return fullReplace;
  }

  /**
   * Propose-time inline-reference preflight for one uniquely-found edit. Mirrors
   * the apply path's guard, but narrows to the SMALLEST sub-range that ANY of the
   * candidate replacements would touch: the changed middle is computed across
   * [new_text, ...alternatives[].text] (the intersection of each candidate's
   * common prefix/suffix, so the sub-range covers what the riskiest alternative
   * would change). That narrowest range is sub-searched inside the matched range
   * and handed to the same _checkInlineReferences guard the Replace path uses, so
   * an edit whose change would destroy a field / footnote / bookmark is flagged
   * at PROPOSE time rather than one click later on apply. Pure appends and
   * pure-insertions-after-a-prefix carry no inline-ref risk and short-circuit to
   * null. Returns null when the change is clean or no usable sub-range resolves
   * (in which case findability alone governs validity).
   * @param {Word.RequestContext} context - The active request context
   * @param {Word.Range} matchedRange - The unique range found for old_text
   * @param {string} oldText - The edit's old_text
   * @param {string[]} newTexts - [new_text, ...alternatives[].text] candidates
   * @returns {Promise<string|null>}
   */
  async _checkProposeInlineRefs(context, matchedRange, oldText, newTexts) {
    // Diff bounds across ALL candidates: take the SHORTEST shared prefix/suffix
    // so aMid widens to cover whatever the riskiest alternative would change.
    let minPre = Infinity;
    let minSuf = Infinity;
    for (const t of newTexts) {
      if (typeof t !== 'string') continue;
      const d = computeDiffBounds(oldText, t);
      minPre = Math.min(minPre, d.pre);
      minSuf = Math.min(minSuf, d.suf);
    }
    if (!Number.isFinite(minPre)) return null;
    const aMid = oldText.slice(minPre, oldText.length - minSuf);

    // Pure append (entire old_text is the shared prefix) cannot destroy a
    // reference inside old_text: nothing in old_text is replaced.
    if (aMid.length === 0 && minPre === oldText.length) return null;

    // Sub-search the narrowest range, mirroring _computeReplaceLocation's
    // trySubSearch (exact then smart-punct fallback, matchCase like the apply
    // path). Falls back to the full matched range when no tight sub-range
    // resolves, so a real reference is never missed.
    const trySubSearch = async (searchText) => {
      if (searchText.length > MAX_ANCHOR_CHARS || searchText.includes('\n')) return null;
      const hits = matchedRange.search(searchText, { matchCase: true });
      hits.load('items');
      await context.sync();
      if (hits.items.length === 1) return hits.items[0];
      const smart = normalizeToSmartPunct(searchText);
      if (smart === searchText) return null;
      const smartHits = matchedRange.search(smart, { matchCase: true });
      smartHits.load('items');
      await context.sync();
      return smartHits.items.length === 1 ? smartHits.items[0] : null;
    };

    let probeRange = null;
    if (aMid.length >= 4) {
      probeRange = await trySubSearch(aMid);
    } else if (aMid.length === 0 && minPre >= 4) {
      // Pure insertion after a resolvable prefix: nothing in old_text is
      // replaced, so the references inside it survive. No guard needed.
      const prefixRange = await trySubSearch(oldText.slice(0, minPre));
      if (prefixRange) return null;
    }

    // Guard the narrowest range we could resolve; otherwise the full match.
    return this._checkInlineReferences(context, probeRange || matchedRange);
  }

  /**
   * Best-effort inline-reference guard. Loads fields, bookmarks, foot/endnotes
   * inside a range that is about to be fully Replaced and returns a descriptive
   * error if the Replace would destroy any of them (so the model can edit one
   * plain-text side of the reference instead). Returns null when the range is
   * clean or the host lacks range-field support.
   * @param {Word.RequestContext} context - The active request context
   * @param {Word.Range} range - The range about to be replaced
   * @returns {Promise<string|null>}
   */
  async _checkInlineReferences(context, range) {
    const fields = range.fields;
    fields.load('items');
    const bookmarks = range.getBookmarks(true, true);
    let footnotes = null;
    let endnotes = null;
    try {
      footnotes = range.footnotes;
      endnotes = range.endnotes;
      footnotes.load('items');
      endnotes.load('items');
    } catch (e) {
      footnotes = null;
      endnotes = null;
    }
    await context.sync();

    const parts = [];
    if (fields.items.length > 0) {
      parts.push(`${fields.items.length} field(s) (cross-references or page numbers)`);
    }
    if (footnotes && footnotes.items.length > 0) {
      parts.push(`${footnotes.items.length} footnote marker(s)`);
    }
    if (endnotes && endnotes.items.length > 0) {
      parts.push(`${endnotes.items.length} endnote marker(s)`);
    }
    if (bookmarks.value && bookmarks.value.length > 0) {
      parts.push(`bookmark(s): ${bookmarks.value.join(', ')}`);
    }
    if (parts.length === 0) return null;

    return (
      `old_text overlaps inline references that Replace would destroy: ${parts.join('; ')}. ` +
      'Shorten old_text to the plain-text fragment on ONE side of the reference, ' +
      'or fall back to execute_office_js and edit each side separately.'
    );
  }

  /**
   * Post-edit TrackedChangeGuard. After a replacement, if change tracking is on
   * but the edited paragraph's OOXML carries no revision markup, the edit
   * landed directly instead of as a redline; return a warning so the model can
   * tell the user. Returns undefined when tracking is off or markup is present.
   * @param {Word.RequestContext} context - The active request context
   * @param {Word.Range} range - The post-edit range
   * @returns {Promise<string|undefined>}
   */
  async _checkTrackedChangeWarning(context, range) {
    let changeTrackingMode;
    let ooxml;
    try {
      context.document.load('changeTrackingMode');
      const ooxmlResult = range.paragraphs.getFirst().getOoxml();
      await context.sync();
      changeTrackingMode = context.document.changeTrackingMode;
      ooxml = ooxmlResult.value;
    } catch (e) {
      return undefined;
    }

    if (changeTrackingMode && changeTrackingMode !== 'Off' && !TRACKED_CHANGE_OOXML_RE.test(String(ooxml || ''))) {
      return (
        `Track Changes is on (${changeTrackingMode}) but this edit landed without revision markup - ` +
        'it was applied directly, not as a redline. Tell the user the change was made directly instead of ' +
        'as a tracked change, and offer to undo it (Ctrl+Z).'
      );
    }
    return undefined;
  }

  /**
   * Reapply a font snapshot to a range, skipping Word's "Mixed" sentinel and
   * null/undefined values so a heterogeneous source range does not clobber the
   * replacement with a single uniform style.
   * @param {Word.Range} range - Range to restyle
   * @param {Object} snapshot - Font properties captured before the replace
   */
  _restoreFont(range, snapshot) {
    if (!snapshot) return;
    const font = range.font;
    const set = (key, value) => {
      if (value == null || value === 'Mixed') return;
      font[key] = value;
    };
    set('bold', snapshot.bold);
    set('italic', snapshot.italic);
    set('underline', snapshot.underline);
    set('color', snapshot.color);
    set('size', snapshot.size);
    set('name', snapshot.name);
    set('highlightColor', snapshot.highlightColor);
  }

  // ===========================================================================
  // TOOL: edit_doc_list (create a list, or insert one item without renumbering)
  // ===========================================================================

  /**
   * Create a simple bullet/number list after an anchor paragraph, or insert a
   * single item into an existing list without restarting numbering.
   *
   *  - create_list: needs anchor_text + items + list_style. Each item is
   *    inserted as a new paragraph after the anchor and styled "List Bullet" /
   *    "List Number" (Word builds/continues the list from the style).
   *  - insert_item: needs anchor_item_text + new_item_text + position. The
   *    anchor must already be a list item; the new paragraph is inserted Before
   *    or After it and, if Word does not auto-join the list, attachToList() is
   *    used to bind it to the anchor's list id and level.
   *
   * @param {Object} params
   * @param {string} [params.action] - 'create_list' | 'insert_item'
   * @param {string} [params.anchor_text] - create_list: unique anchor paragraph text
   * @param {string[]} [params.items] - create_list: item texts in order (no prefixes)
   * @param {string} [params.list_style] - create_list: 'bullet' | 'number'
   * @param {string} [params.anchor_item_text] - insert_item: unique existing list item text
   * @param {string} [params.new_item_text] - insert_item: new item text (no prefix)
   * @param {string} [params.position] - insert_item: 'Before' | 'After'
   * @returns {Promise<Object>} - { success, action, items:[{text,isListItem,level,listString}], error? }
   */
  async editDocList({ action, anchor_text, items, list_style, anchor_item_text, new_item_text, position }) {
    if (typeof Word === 'undefined') {
      throw new Error('Word API not available');
    }

    if (action === 'create_list') {
      return this._createList({ anchor_text, items, list_style });
    }
    if (action === 'insert_item') {
      return this._insertListItem({ anchor_item_text, new_item_text, position });
    }
    throw new Error(`Unknown action: ${action}. Expected 'create_list' or 'insert_item'.`);
  }

  /**
   * create_list branch of edit_doc_list.
   * @param {Object} params - { anchor_text, items, list_style }
   * @returns {Promise<Object>}
   */
  async _createList({ anchor_text, items, list_style }) {
    if (typeof anchor_text !== 'string' || anchor_text.length === 0) {
      throw new Error('anchor_text is required for create_list');
    }
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error('items must be a non-empty array of strings for create_list');
    }
    const styleName = LIST_STYLE_MAP[list_style];
    if (!styleName) {
      throw new Error("list_style must be 'bullet' or 'number'");
    }
    if (anchor_text.length > MAX_ANCHOR_CHARS) {
      throw new Error(`anchor_text is ${anchor_text.length} chars; Word's search caps at ${MAX_ANCHOR_CHARS}.`);
    }

    return this._safeOfficeRun(async (context) => {
      const match = await this._searchSingle(context, anchor_text, 'anchor_text');
      if (!match) {
        return { success: false, error: `anchor_text not found: "${anchor_text}"` };
      }

      // Insert each item as a new paragraph after the previous one, styled as a
      // list item. Word builds/continues the list from the applied style.
      let current = match.paragraphs.getFirst();
      const newParas = [];
      for (const itemText of items) {
        const newPara = current.insertParagraph(String(itemText ?? ''), Word.InsertLocation.after);
        newPara.style = styleName;
        newParas.push(newPara);
        current = newPara;
      }
      await context.sync();

      const readBack = await this._readBackListItems(context, newParas);
      const failed = readBack.filter((i) => !i.isListItem);
      if (failed.length > 0) {
        return {
          success: false,
          error: `List creation produced ${failed.length} of ${readBack.length} item(s) without a list marker; ` +
            'Word may have rejected the style. Fall back to execute_office_js with the list/attachToList pattern.',
          result: { items: readBack },
        };
      }

      return { success: true, action: 'create_list', items: readBack };
    }, { label: 'edit_doc_list' });
  }

  /**
   * insert_item branch of edit_doc_list.
   * @param {Object} params - { anchor_item_text, new_item_text, position }
   * @returns {Promise<Object>}
   */
  async _insertListItem({ anchor_item_text, new_item_text, position }) {
    if (typeof anchor_item_text !== 'string' || anchor_item_text.length === 0) {
      throw new Error('anchor_item_text is required for insert_item');
    }
    if (typeof new_item_text !== 'string') {
      throw new Error('new_item_text is required for insert_item');
    }
    const where = position === 'Before' ? Word.InsertLocation.before : Word.InsertLocation.after;
    if (position !== 'Before' && position !== 'After') {
      throw new Error("position must be 'Before' or 'After' for insert_item");
    }
    if (anchor_item_text.length > MAX_ANCHOR_CHARS) {
      throw new Error(`anchor_item_text is ${anchor_item_text.length} chars; Word's search caps at ${MAX_ANCHOR_CHARS}.`);
    }

    return this._safeOfficeRun(async (context) => {
      const match = await this._searchSingle(context, anchor_item_text, 'anchor_item_text');
      if (!match) {
        return { success: false, error: `anchor_item_text not found: "${anchor_item_text}"` };
      }

      const anchorPara = match.paragraphs.getFirst();
      const list = anchorPara.listOrNullObject;
      const listItem = anchorPara.listItemOrNullObject;
      anchorPara.load('isListItem');
      list.load('id');
      listItem.load('level');
      await context.sync();

      if (!anchorPara.isListItem) {
        return {
          success: false,
          error: 'Anchor paragraph is not a list item; insert_item attaches to the anchor\'s list, so the anchor ' +
            "must already be in one. For a new list use action: 'create_list'.",
        };
      }

      const newPara = anchorPara.insertParagraph(new_item_text, where);
      newPara.load('isListItem');
      await context.sync();

      // If Word did not auto-join the list, bind explicitly to the anchor's list.
      if (!newPara.isListItem) {
        try {
          newPara.attachToList(list.id, listItem.level);
          await context.sync();
        } catch (attachErr) {
          newPara.delete();
          await context.sync();
          throw new Error(
            'List attachment failed; the new item was inserted as a plain paragraph and has been removed. ' +
            'Fall back to execute_office_js with the attachToList pattern.'
          );
        }
      }

      const readBack = await this._readBackListItems(context, [newPara]);
      const failed = readBack.filter((i) => !i.isListItem);
      if (failed.length > 0) {
        return {
          success: false,
          error: 'List attachment failed; the new item came back without a list marker. ' +
            'Fall back to execute_office_js with the attachToList pattern.',
          result: { items: readBack },
        };
      }

      return { success: true, action: 'insert_item', items: readBack };
    }, { label: 'edit_doc_list' });
  }

  /**
   * Search the body for a single unique occurrence of `text` (exact, then
   * smart-quote fallback). Returns the matched search-result range, or null if
   * absent. Throws if the text matches more than once.
   * @param {Word.RequestContext} context - The active request context
   * @param {string} text - Anchor text (<=255 chars)
   * @param {string} label - Field name for error messages
   * @returns {Promise<Word.Range|null>}
   */
  async _searchSingle(context, text, label) {
    let results = context.document.body.search(text, { matchCase: true });
    results.load('items');
    await context.sync();

    if (results.items.length === 0) {
      // Smart-quote fallback for straight-punctuation anchors.
      const smart = text
        .replace(/(^|[\s([{–\u2014-])"/g, '$1“')
        .replace(/"/g, '”')
        .replace(/(^|[\s([{–\u2014-])'/g, '$1‘')
        .replace(/'/g, '’');
      if (smart !== text) {
        results = context.document.body.search(smart, { matchCase: true });
        results.load('items');
        await context.sync();
      }
    }

    if (results.items.length === 0) return null;
    if (results.items.length > 1) {
      throw new Error(`${label} matched ${results.items.length} ranges; it must be unique.`);
    }
    return results.items[0];
  }

  /**
   * Load text + list state for a set of paragraphs and return plain descriptors.
   * @param {Word.RequestContext} context - The active request context
   * @param {Word.Paragraph[]} paras - Paragraphs to read back
   * @returns {Promise<Array<{ text: string, isListItem: boolean, level: number, listString: string }>>}
   */
  async _readBackListItems(context, paras) {
    const listObjs = [];
    for (const p of paras) {
      p.load('text, isListItem');
      const li = p.listItemOrNullObject;
      li.load('level, listString, isNullObject');
      listObjs.push(li);
    }
    await context.sync();

    return paras.map((p, idx) => {
      const li = listObjs[idx];
      return {
        text: p.text,
        isListItem: p.isListItem,
        level: !li.isNullObject ? li.level : 0,
        listString: !li.isNullObject ? li.listString : '',
      };
    });
  }

  // ===========================================================================
  // TOOL: read_doc_section (by heading, or by paragraph index range)
  // ===========================================================================

  /**
   * Read a section by heading text (reads to the next heading of same-or-higher
   * level) OR by a zero-based paragraph index range. Returns each paragraph as
   * an "[index] text" line plus a structured paragraphs[] array for follow-up
   * edits. Output is capped at MAX_READ_CHARS, with a truncation note.
   *
   * Provide `heading` OR `paragraph_start` (at least one, not both).
   *
   * @param {Object} params
   * @param {string} [params.heading] - Exact heading text to read under
   * @param {number} [params.paragraph_start] - Zero-based start index
   * @param {number} [params.paragraph_end] - Zero-based exclusive end (default start+1)
   * @returns {Promise<Object>} - { success, text, paragraphs:[{index,style,text}], ... }
   */
  async readDocSection({ heading, paragraph_start, paragraph_end }) {
    if (typeof Word === 'undefined') {
      throw new Error('Word API not available');
    }
    const hasHeading = typeof heading === 'string' && heading.length > 0;
    const hasStart = Number.isInteger(paragraph_start);
    if (hasHeading === hasStart) {
      throw new Error('Provide exactly one of heading or paragraph_start (not both, not neither).');
    }

    return this._safeOfficeRun(async (context) => {
      const paragraphs = await this._getParagraphs(context);
      const items = paragraphs.items;
      const total = items.length;

      let start;
      let end;
      if (hasHeading) {
        const found = this._findHeadingParagraph(items, heading);
        if (!found) {
          return { success: false, error: `Heading not found: "${heading}"` };
        }
        start = found.start;
        end = found.end;
      } else {
        start = paragraph_start;
        end = Number.isInteger(paragraph_end) ? paragraph_end : paragraph_start + 1;
        if (start < 0 || start >= total) {
          return { success: false, error: `paragraph_start ${start} is out of bounds (document has ${total} paragraphs)` };
        }
        end = Math.min(Math.max(end, start + 1), total);
      }

      const lines = [];
      const paraOut = [];
      let text = '';
      let truncated = null;

      for (let i = start; i < end; i++) {
        const p = items[i];
        const line = `[${i}] ${p.text}`;
        if (text.length + line.length + 1 > MAX_READ_CHARS && paraOut.length > 0) {
          truncated = `Output capped at ${MAX_READ_CHARS} chars at paragraph ${i}; ` +
            `re-call read_doc_section with paragraph_start: ${i} to continue.`;
          break;
        }
        lines.push(line);
        paraOut.push({ index: i, style: p.styleBuiltIn, text: p.text });
        text = lines.join('\n');
      }

      return {
        success: true,
        text,
        paragraphs: paraOut,
        paragraphStart: start,
        paragraphEnd: start + paraOut.length,
        paragraphCount: paraOut.length,
        truncated: truncated || undefined,
      };
    }, { label: 'read_doc_section' });
  }

  // ===========================================================================
  // TOOL: collapse_blank_paragraphs
  // ===========================================================================

  /**
   * Collapse runs of consecutive empty paragraphs down to `max_consecutive`,
   * deleting in reverse-order batches so large documents do not time out, and
   * skipping any "blank" paragraph whose OOXML carries structurally-meaningful
   * content (drawing, picture, object, section break, bookmark, tracked change,
   * comment range). A paragraph counts as blank only if its text is empty AND it
   * is not a list item AND it is not inside a table.
   *
   * @param {Object} params
   * @param {number} [params.max_consecutive=1] - Allowed run length (0..2)
   * @returns {Promise<Object>} - { success, removed }
   */
  async collapseBlankParagraphs({ max_consecutive = 1 } = {}) {
    if (typeof Word === 'undefined') {
      throw new Error('Word API not available');
    }
    const cap = Math.max(0, Math.min(2, Number.isInteger(max_consecutive) ? max_consecutive : 1));

    return this._safeOfficeRun(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load('items/text, items/isListItem');
      await context.sync();

      const items = paragraphs.items;

      // Per-paragraph table membership: a paragraph inside a table is never blank.
      const tableChecks = items.map((p) => {
        const t = p.parentTableOrNullObject;
        t.load('isNullObject');
        return t;
      });
      await context.sync();

      const isBlank = items.map((p, i) =>
        String(p.text || '').trim() === '' && !p.isListItem && tableChecks[i].isNullObject
      );

      // Walk runs of blank paragraphs; mark everything beyond `cap` for deletion.
      const toDelete = [];
      let runStart = -1;
      for (let i = 0; i <= items.length; i++) {
        if (i < items.length && isBlank[i]) {
          if (runStart === -1) runStart = i;
        } else if (runStart !== -1) {
          for (let j = runStart + cap; j < i; j++) toDelete.push(j);
          runStart = -1;
        }
      }

      // Delete in reverse, in batches. Per batch, re-check OOXML and SKIP any
      // paragraph that actually carries structural content.
      let removed = 0;
      let skipped = 0;
      const reverse = toDelete.slice().sort((a, b) => b - a);

      for (let batchStart = 0; batchStart < reverse.length; batchStart += BLANK_PARA_BATCH_SIZE) {
        const batch = reverse.slice(batchStart, batchStart + BLANK_PARA_BATCH_SIZE);
        const ooxmlResults = batch.map((idx) => items[idx].getOoxml());
        await context.sync();

        for (let k = 0; k < batch.length; k++) {
          const idx = batch[k];
          const ooxml = String(ooxmlResults[k].value || '');
          if (STRUCTURAL_OOXML_RE.test(ooxml)) {
            skipped += 1;
            continue;
          }
          items[idx].delete();
          removed += 1;
        }
        await context.sync();
      }

      return { success: true, removed, scanned: items.length, skipped_with_content: skipped };
    }, { label: 'collapse_blank_paragraphs' });
  }

  // ===========================================================================
  // TOOL: verify_doc (structural check, no LLM)
  // ===========================================================================

  /**
   * Fast structural check between edits: paragraph style distribution, heading
   * outline, list-numbering sequence, and blank-paragraph runs. No screenshot,
   * no model call. Flags numbering restarts and large blank runs in `issues`.
   *
   * @returns {Promise<Object>} - { success, paragraphCount, styles, headings, issues }
   */
  async verifyDoc() {
    if (typeof Word === 'undefined') {
      throw new Error('Word API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const body = context.document.body;
      const paragraphs = body.paragraphs;
      paragraphs.load('items/text, items/styleBuiltIn, items/isListItem');

      // Per-table row/cell shape (best-effort): each row's cell count lets us
      // flag ragged tables. Guarded so an unsupported host degrades gracefully.
      let tableData = null;
      try {
        const tables = body.tables;
        tables.load('items/rows/items/cellCount');
        tableData = tables;
      } catch (e) {
        tableData = null;
      }
      await context.sync();

      const items = paragraphs.items;

      // Second sync to load list level/string for the numbering check.
      const listObjs = items.map((p) => {
        const li = p.listItemOrNullObject;
        li.load('level, listString, isNullObject');
        return li;
      });
      await context.sync();

      const styles = {};
      const headings = [];
      const lists = [];
      const issues = [];

      // Build the per-table shape descriptors (uniform = every row has the same
      // cell count). Empty/failed table load yields an empty array.
      const tables = [];
      if (tableData) {
        try {
          tableData.items.forEach((t, tableIndex) => {
            const cellsPerRow = t.rows.items.map((r) => r.cellCount);
            tables.push({
              tableIndex,
              rowCount: cellsPerRow.length,
              cellsPerRow,
              uniform: cellsPerRow.length > 0 && cellsPerRow.every((c) => c === cellsPerRow[0]),
            });
          });
        } catch (e) {
          // Table shape unavailable on this host; leave tables empty.
        }
      }

      let blankRun = 0;
      for (let i = 0; i < items.length; i++) {
        const p = items[i];
        const styleName = p.styleBuiltIn || 'Normal';
        styles[styleName] = (styles[styleName] || 0) + 1;

        const level = parseHeadingLevel(p.styleBuiltIn);
        if (level != null && level <= MAX_OUTLINE_HEADING_LEVEL) {
          headings.push({ level, text: String(p.text || '').trim(), index: i });
        }

        const li = listObjs[i];
        if (!li.isNullObject) {
          lists.push({ paraIndex: i, level: li.level, listString: li.listString });
        }

        if (String(p.text || '').trim() === '' && !p.isListItem) {
          blankRun += 1;
          if (blankRun === 3) {
            issues.push(`3+ consecutive blank paragraphs starting near paragraph ${i - 2}; consider collapse_blank_paragraphs.`);
          }
        } else {
          blankRun = 0;
        }
      }

      // Outline sanity: a heading level jumping by more than one (e.g. H1 -> H3).
      for (let h = 1; h < headings.length; h++) {
        if (headings[h].level - headings[h - 1].level > 1) {
          issues.push(
            `Heading outline jumps from H${headings[h - 1].level} to H${headings[h].level} at "${headings[h].text}".`
          );
        }
      }

      return {
        success: true,
        paragraphCount: items.length,
        styles,
        headings,
        lists,
        tables,
        issues,
      };
    }, { label: 'verify_doc' });
  }

  /**
   * Append bounded licensed-data content at the document end. Only native Word
   * paragraphs and tables are used, and every appended value is read back
   * before safe structural metadata is returned.
   */
  async appendFinanceWordContent(args, options = {}) {
    const request = validateFinanceWordContentRequest(args);
    if (typeof Word === 'undefined') {
      throw new Error('Word API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const body = context.document.body;
      const insertedParagraphs = request.paragraphs.map((text) => {
        const paragraph = body.insertParagraph(text, Word.InsertLocation.end);
        paragraph.load('text');
        return { paragraph, expected: text };
      });

      let insertedTable = null;
      let expectedTableValues = null;
      if (request.table) {
        expectedTableValues = [request.table.headers, ...request.table.rows];
        insertedTable = body.insertTable(
          expectedTableValues.length,
          request.table.headers.length,
          Word.InsertLocation.end,
          expectedTableValues
        );
        insertedTable.load('values,rowCount,columnCount');
      }
      await context.sync();

      const paragraphsVerified = insertedParagraphs.every(({ paragraph, expected }) =>
        String(paragraph.text || '').replace(/\r$/, '') === expected
      );
      const tableVerified = !insertedTable || (
        insertedTable.rowCount === expectedTableValues.length &&
        insertedTable.columnCount === expectedTableValues[0].length &&
        expectedTableValues.every((row, rowIndex) => row.every((expected, columnIndex) =>
          insertedTable.values?.[rowIndex]?.[columnIndex] === expected
        ))
      );
      if (!paragraphsVerified || !tableVerified) {
        throw new Error('Finance Word content verification failed');
      }

      return {
        success: true,
        verifiedNativeWrite: true,
        paragraphsAppended: request.paragraphs.length,
        tableRowsAppended: expectedTableValues?.length || 0,
        tableColumns: expectedTableValues?.[0]?.length || 0,
      };
    }, { label: 'append_finance_word_content', ...options });
  }

  /**
   * Attach code-issued Daloopa source markers as native Word paragraphs. The
   * operation is idempotent and reads the document back before acknowledging
   * that any citation was attached.
   */
  async attachConnectorCitations({ citations, target }, options = {}) {
    const safeCitations = validateDaloopaCitations(citations);
    validateDaloopaWordTarget(target);
    if (typeof Word === 'undefined') {
      throw new Error('Word API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const body = context.document.body;
      const existingParagraphs = body.paragraphs;
      existingParagraphs.load('items/text');
      await context.sync();

      const markersToReplace = new Set(safeCitations.map((citation) => citation.marker));
      const existingMarkerParagraphs = existingParagraphs.items.filter((paragraph) =>
        markersToReplace.has(String(paragraph.text || '').trim())
      );
      for (const paragraph of existingMarkerParagraphs) paragraph.delete();
      if (existingMarkerParagraphs.length > 0) await context.sync();

      const markerParagraphs = [];
      for (const citation of safeCitations) {
        const paragraph = body.insertParagraph(citation.marker, Word.InsertLocation.end);
        paragraph.styleBuiltIn = 'Caption';
        paragraph.font.hidden = false;
        paragraph.font.size = 8;
        paragraph.font.color = '#595959';
        paragraph.font.bold = false;
        paragraph.font.italic = false;
        paragraph.load('text,styleBuiltIn');
        paragraph.font.load('hidden,size,color,bold,italic');
        markerParagraphs.push({ paragraph, citation });
      }
      await context.sync();

      const visibleAndVerified = markerParagraphs.every(({ paragraph, citation }) =>
        String(paragraph.text || '').trim() === citation.marker &&
        paragraph.styleBuiltIn === 'Caption' &&
        paragraph.font.hidden === false &&
        paragraph.font.size === 8 &&
        String(paragraph.font.color || '').toUpperCase() === '#595959' &&
        paragraph.font.bold === false &&
        paragraph.font.italic === false
      );
      if (!visibleAndVerified) {
        throw new Error('Daloopa source marker verification failed in Word');
      }

      return {
        success: true,
        attachedCitationIds: safeCitations.map((citation) => citation.citationId),
      };
    }, { label: 'attach_connector_citations', ...options });
  }

  async _readDocumentPdf() {
    if (typeof Office === 'undefined' || !Office.context?.document?.getFileAsync) {
      throw new Error('Word PDF export is not available on this host');
    }
    return new Promise((resolve, reject) => {
      Office.context.document.getFileAsync(
        Office.FileType.Pdf,
        { sliceSize: 4 * 1024 * 1024 },
        (fileResult) => {
          if (fileResult.status !== Office.AsyncResultStatus.Succeeded) {
            reject(new Error(fileResult.error?.message || 'Word PDF export failed'));
            return;
          }
          const file = fileResult.value;
          const chunks = [];
          let sliceIndex = 0;

          const closeAndReject = (error) => {
            try { file.closeAsync(() => reject(error)); } catch { reject(error); }
          };
          const readNext = () => {
            file.getSliceAsync(sliceIndex, (sliceResult) => {
              if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
                closeAndReject(new Error(sliceResult.error?.message || 'Could not read Word PDF slice'));
                return;
              }
              const data = sliceResult.value?.data;
              const bytes = data instanceof Uint8Array
                ? data
                : data instanceof ArrayBuffer
                  ? new Uint8Array(data)
                  : Uint8Array.from(data || []);
              chunks.push(bytes);
              sliceIndex += 1;
              if (sliceIndex < file.sliceCount) {
                readNext();
                return;
              }
              const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
              const merged = new Uint8Array(total);
              let offset = 0;
              for (const chunk of chunks) {
                merged.set(chunk, offset);
                offset += chunk.length;
              }
              file.closeAsync(() => resolve(merged));
            });
          };
          readNext();
        }
      );
    });
  }

  /** Render up to three document pages for visual verification. */
  async screenshotDocument({ pages = [1] } = {}) {
    const pdfBytes = await this._readDocumentPdf();
    const pdfjs = await import('pdfjs-dist/webpack');
    const pdf = await pdfjs.getDocument({ data: pdfBytes }).promise;
    const requestedPages = [...new Set((pages || [1]).map(Number))]
      .filter((page) => Number.isInteger(page) && page >= 1 && page <= pdf.numPages)
      .slice(0, 3);
    if (requestedPages.length === 0) {
      throw new Error(`No requested page exists. This document has ${pdf.numPages} page(s).`);
    }

    const content = [{ type: 'text', text: `Rendered page(s) ${requestedPages.join(', ')} of ${pdf.numPages}.` }];
    for (const pageNumber of requestedPages) {
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const canvasContext = canvas.getContext('2d');
      await page.render({ canvasContext, viewport }).promise;
      content.push({
        type: 'image',
        source: {
          type: 'base64',
          media_type: 'image/png',
          data: canvas.toDataURL('image/png').split(',')[1],
        },
      });
    }
    return { content };
  }

  // ===========================================================================
  // CONTEXT: getInitialState (document metadata for context injection)
  // ===========================================================================

  /**
   * Gather document metadata sent with every user message as the <doc_state>
   * block. Parallel to ExcelBridge.getInitialState: returns null on any
   * top-level failure rather than throwing, so context injection degrades
   * gracefully. Individual enrichments (comments, tracked changes) are each
   * try/caught so an unsupported host simply omits the field.
   *
   * Payload:
   *  - fileName, wordCount, characterCount, paragraphCount
   *  - changeTrackingMode (literal "Off" | "TrackAll" | "TrackMineOnly") plus a
   *    back-compat trackChanges boolean
   *  - headings: [{ level, text, index }] (zero-based paragraph index)
   *  - styles: map of paragraph styleName -> count (styles in use)
   *  - comments: [{ id, author, anchorPreview }] (WordApi 1.4+, guarded)
   *  - trackedChanges: [{ id, type, author }] (WordApi 1.4+, guarded)
   *  - displayLanguage
   * @returns {Promise<Object|null>} - Document metadata, or null
   */
  async getInitialState() {
    if (typeof Word === 'undefined') {
      return null;
    }

    try {
      return await this._safeOfficeRun(async (context) => {
        const body = context.document.body;
        const paragraphs = body.paragraphs;
        body.load('text');
        paragraphs.load('items/text, items/styleBuiltIn');

        const properties = context.document.properties;
        properties.load('title');

        try {
          context.document.load('changeTrackingMode');
        } catch (e) {
          // changeTrackingMode unsupported on this host; left undefined below.
        }

        // Comments + tracked changes (WordApi 1.4+). Each is loaded under its
        // own guard so an older host omits the field instead of failing.
        const comments = await this._loadComments(context, body);
        const trackedChanges = await this._loadTrackedChanges(context, body);

        await context.sync();

        // Resolve change-tracking mode as the literal string the prompt expects.
        let changeTrackingMode = 'Off';
        try {
          changeTrackingMode = context.document.changeTrackingMode || 'Off';
        } catch (e) {
          changeTrackingMode = 'Off';
        }
        const trackChanges = changeTrackingMode !== 'Off';

        // Outline + style distribution in one pass over the paragraphs.
        const headings = [];
        const styles = {};
        for (let i = 0; i < paragraphs.items.length; i++) {
          const styleName = paragraphs.items[i].styleBuiltIn || 'Normal';
          styles[styleName] = (styles[styleName] || 0) + 1;
          const level = parseHeadingLevel(paragraphs.items[i].styleBuiltIn);
          if (level != null && level <= MAX_OUTLINE_HEADING_LEVEL) {
            headings.push({ level, text: String(paragraphs.items[i].text || '').trim(), index: i });
          }
        }

        // Word + character counts: split the body text on whitespace (a cheap
        // approximation that avoids a separate getStatistics round-trip).
        const fullText = String(body.text || '');
        const wordCount = fullText.trim() ? fullText.trim().split(/\s+/).length : 0;
        const characterCount = fullText.length;

        const docUrl = (typeof Office !== 'undefined' && Office.context?.document?.url) || '';
        const fileNameFromUrl = docUrl ? (docUrl.split('/').pop().split('?')[0] || '') : '';
        const fileName = properties.title || fileNameFromUrl || 'Document';

        const state = {
          fileName,
          wordCount,
          characterCount,
          paragraphCount: paragraphs.items.length,
          headings,
          styles,
          changeTrackingMode,
          trackChanges,
          displayLanguage: typeof Office !== 'undefined' ? (Office.context?.displayLanguage || null) : null,
        };
        if (comments) state.comments = comments;
        if (trackedChanges) state.trackedChanges = trackedChanges;
        return state;
      });
    } catch (error) {
      console.warn('[WordBridge] getInitialState failed:', error.message);
      return null;
    }
  }

  /**
   * Enumerate document comments for <doc_state> (WordApi 1.4+). Returns an
   * array of { id, author, anchorPreview } (anchorPreview = first ~80 chars of
   * the comment's content range text), or null when comments are unsupported.
   * Issues its own context.sync() so the anchor text is available.
   * @param {Word.RequestContext} context - The active request context
   * @param {Word.Body} body - The document body
   * @returns {Promise<Array<{ id: string, author: string, anchorPreview: string }>|null>}
   */
  async _loadComments(context, body) {
    try {
      if (typeof Office !== 'undefined' &&
        Office.context?.requirements?.isSetSupported &&
        !Office.context.requirements.isSetSupported('WordApi', '1.4')) {
        return null;
      }
      const comments = body.getComments();
      comments.load('items/id, items/authorName');
      await context.sync();

      const ranges = comments.items.map((c) => {
        const r = c.getRange();
        r.load('text');
        return r;
      });
      await context.sync();

      return comments.items.map((c, i) => ({
        id: c.id,
        author: c.authorName || '',
        anchorPreview: String(ranges[i].text || '').slice(0, 80),
      }));
    } catch (e) {
      return null;
    }
  }

  /**
   * Enumerate tracked changes for <doc_state> (WordApi 1.4+). Returns an array
   * of { id, type, author }, or null when tracked changes are unsupported.
   * @param {Word.RequestContext} context - The active request context
   * @param {Word.Body} body - The document body
   * @returns {Promise<Array<{ id: string, type: string, author: string }>|null>}
   */
  async _loadTrackedChanges(context, body) {
    try {
      if (typeof Office !== 'undefined' &&
        Office.context?.requirements?.isSetSupported &&
        !Office.context.requirements.isSetSupported('WordApi', '1.4')) {
        return null;
      }
      const tcs = body.getTrackedChanges();
      tcs.load('items/id, items/type, items/author');
      await context.sync();

      return tcs.items.map((tc) => ({
        id: tc.id,
        type: tc.type,
        author: tc.author || '',
      }));
    } catch (e) {
      return null;
    }
  }

  // ===========================================================================
  // CONTEXT: getUserContext (current selection text + style)
  // ===========================================================================

  /**
   * Get the current selection text and its paragraph style for <user_context>
   * injection. Returns null on failure.
   * @returns {Promise<Object|null>} - { selectedText, selectionStyle }, or null
   */
  async getUserContext() {
    if (typeof Word === 'undefined') {
      return null;
    }

    // Office on the web: skip the selection probe. It is a non-essential
    // <user_context> nicety, but it runs through the SAME serialized Word.run
    // queue as the real tools. On the web host this probe's Word.run can hang
    // (observed: 120s timeout), and because runs are serialized, that hang
    // poisons the queue so every subsequent tool call times out waiting behind
    // it. Skipping it on the web gives the first real tool run a clean,
    // unblocked shot at the host.
    if (this._isOfficeOnline) {
      console.log('[WordBridge] getUserContext skipped on Office web (avoids run-queue stall)');
      return null;
    }

    try {
      return await this._safeOfficeRun(async (context) => {
        const selection = context.document.getSelection();
        selection.load('text, styleBuiltIn');
        const selParas = selection.paragraphs;
        selParas.load('items');
        const bodyParas = context.document.body.paragraphs;
        bodyParas.load('items');
        await context.sync();

        const text = selection.text || '';
        const selCount = selParas.items.length;
        const bodyCount = bodyParas.items.length;

        // Classify the selection the way the reference <user_selection> does:
        // cursor / single_paragraph / multi_paragraph / whole_document. For
        // multi-paragraph and whole-document we do NOT inline the full text (it
        // can be huge); the model calls getSelection() via execute_office_js if
        // it needs the body.
        let kind;
        let selectedText = text;
        if (!text || text.trim() === '') {
          kind = 'cursor';
          selectedText = '';
        } else if (bodyCount > 0 && selCount >= bodyCount) {
          kind = 'whole_document';
          selectedText = `Whole document selected (${selCount} paragraphs). Content not included; call getSelection() to read it.`;
        } else if (selCount > 1) {
          kind = 'multi_paragraph';
          selectedText = `Multi-paragraph selection (${selCount} paragraphs). Content not included; call getSelection() to read it.`;
        } else {
          kind = 'single_paragraph';
        }

        return {
          kind,
          selectedText,
          selectionStyle: selection.styleBuiltIn || null,
          paragraphCount: selCount,
        };
      });
    } catch (error) {
      console.warn('[WordBridge] getUserContext failed:', error.message);
      return null;
    }
  }

  /**
   * Validate a set of proposed edits WITHOUT applying any of them. Two gates per
   * edit, both keyed to the real edit_doc_text apply path:
   *  1. Findability: confirm old_text is uniquely findable in the document as it
   *     stands now (reuses the same _findUniqueRange search the apply path uses).
   *  2. Inline-reference preflight: on each findable edit, run the same
   *     inline-reference guard the Replace path runs, but on the narrowest
   *     sub-range that ANY candidate replacement (new_text or any alternative)
   *     would touch. An edit whose change would destroy a field / footnote /
   *     bookmark / cross-reference is flagged invalid HERE (at propose time)
   *     rather than one click later on apply, so the review card disables the
   *     row up front. This gate is best-effort: any probe failure degrades to
   *     findability-only validity rather than throwing.
   * This powers the propose_doc_edits tool, so the model can stage substantive
   * edits and tell the user which would actually apply (and which are stale or
   * reference-destructive) before anything touches the document. Applies nothing.
   * @param {Object} params
   * @param {Array<{id?:string, old_text:string, new_text?:string, change_summary?:string, alternatives?:Array<{label?:string, text:string}>}>} params.edits
   * @returns {Promise<{ success: boolean, allValid: boolean, edits: Array<{id:string, valid:boolean, error?:string}> }>}
   */
  async proposeDocEdits({ edits } = {}) {
    if (typeof Word === 'undefined') {
      throw new Error('Word API not available');
    }
    if (!Array.isArray(edits) || edits.length === 0) {
      throw new Error('edits must be a non-empty array');
    }

    return this._safeOfficeRun(async (context) => {
      const results = [];
      for (let i = 0; i < edits.length; i++) {
        const edit = edits[i] || {};
        const id = edit.id || `edit-${i + 1}`;
        if (typeof edit.old_text !== 'string' || edit.old_text === '') {
          results.push({ id, valid: false, error: 'old_text is required and must be a non-empty string.' });
          continue;
        }
        try {
          const found = await this._findUniqueRange(context, edit.old_text);
          if (!found || !found.range) {
            results.push({ id, valid: false, error: (found && found.error) || 'old_text is not uniquely findable.' });
            continue;
          }

          // Inline-reference preflight on the narrowest changed sub-range across
          // new_text + every alternative. Best-effort: a probe failure here must
          // not demote a findable edit, so we fall back to valid on any throw.
          let inlineRefWarning = null;
          try {
            const candidateTexts = [
              edit.new_text,
              ...(Array.isArray(edit.alternatives) ? edit.alternatives : []).map((a) => a && a.text),
            ].filter((t) => typeof t === 'string');
            if (candidateTexts.length > 0) {
              inlineRefWarning = await this._checkProposeInlineRefs(
                context, found.range, edit.old_text, candidateTexts
              );
            }
          } catch (e) {
            // Inline-ref probe unsupported on this host or failed: degrade to
            // findability-only validity (mirrors the apply path's soft guard).
            inlineRefWarning = null;
          }

          if (inlineRefWarning) {
            results.push({ id, valid: false, error: inlineRefWarning });
          } else {
            results.push({ id, valid: true });
          }
        } catch (err) {
          results.push({ id, valid: false, error: err.message || 'validation failed' });
        }
      }
      return {
        success: true,
        allValid: results.every((r) => r.valid),
        edits: results,
      };
    }, { label: 'propose_doc_edits' });
  }

  /**
   * apply_template: insert the content of a template .docx/.dotx (base64)
   * at the end of the open document, bringing its structure and formatting
   * along, so the agent can draft on the template's skeleton.
   */
  async applyTemplateFromBase64(base64) {
    if (typeof Word === 'undefined') {
      throw new Error('Word API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const body = context.document.body;
      body.load('text');
      const bodyOoxml = body.getOoxml();
      await context.sync();

      if (!bodyOoxml || typeof bodyOoxml.value !== 'string') {
        throw new Error('Template application refused because the current Word document could not be verified as blank.');
      }
      if (hasMeaningfulWordBodyContent(body.text, bodyOoxml.value)) {
        throw new Error(
          'Template application refused because the current Word document already contains text, tables, images, fields, or other structure.'
        );
      }

      body.insertFileFromBase64(base64, Word.InsertLocation.end);
      await context.sync();

      return {
        success: true,
        note: 'Template content inserted at the end of the document with its formatting. Build on this structure.',
      };
    }, { label: 'apply_template', timeoutMs: 120000 });
  }

}
